import React, { useEffect, useMemo, useState } from "react";
import { numberFormat } from "../../../util/araknerdlib";
import { useStat } from "../../../DataStore/StatContext";
import { useCurrency } from "../../../util/SystemVariables";
import { Skeleton, SkeletonBar, SkeletonPill } from "../../../util/skeleton/Skeleton";
import { Link } from "react-router-dom";
import FinanceCardHead from "../FinanceCardHead";

/* --------- small ui helpers (global CSS) --------- */
const Chip = ({ icon, children, tone = "neutral" }) => (
  <span className={`yx-chip ${tone ? `yx-chip--${tone}` : ""}`}>
    {icon ? <i className={`fa ${icon}`} /> : null}
    <span>{children}</span>
  </span>
);

const Bar = ({ valuePct = 0, tone = "indigo" }) => {
  const pct = Math.max(0, Math.min(100, Number(valuePct) || 0));
  return (
    <div className="yx-bar">
      <div
        className={`yx-bar__fill yx-bar__fill--${tone}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const ProfitAndLossCard = () => {
  const {
    profitLoss,
    income = 0,
    expense = 0,
    net = 0,
    priorNet = null,
    uncategorizedCount = 0,
    reconciledPct = null,
    loadingProfitLoss,
  } = useStat();

  const currency = useCurrency();
  const [incPct, setIncPct] = useState(0);
  const [expPct, setExpPct] = useState(0);


  useEffect(() => {
    if (!profitLoss) return;
    const d = Math.max(Number(income) + Number(expense), 1);
    setIncPct(Math.round((Number(income) / d) * 100));
    setExpPct(Math.round((Number(expense) / d) * 100));
  }, [income, expense, profitLoss]);

  const delta = useMemo(() => {
    if (priorNet === null || priorNet === undefined) return null;
    if (Math.abs(Number(priorNet)) < 1) return "N/A";
    const pct = ((Number(net) - Number(priorNet)) / Math.abs(Number(priorNet))) * 100;
    return `${pct > 0 ? "▲" : "▼"} ${Math.abs(pct).toFixed(1)}%`;
  }, [net, priorNet]);

  const expenseRatio = useMemo(
    () => (Number(income) > 0 ? Math.round((Number(expense) / Number(income)) * 100) : 0),
    [income, expense]
  );

  const hasUncat = Number(uncategorizedCount) > 0;
  const isLoading = loadingProfitLoss ?? !profitLoss;

  /* ---------------- Skeleton ---------------- */
  if (isLoading) {
    return (
      <div className="yx-card">
        <div className="yx-card-head d-flex align-items-start justify-content-between">
          <div style={{ minWidth: 0 }}>
            <Skeleton w="120px" h={10} />
            <Skeleton w="240px" h={22} style={{ marginTop: 10 }} />
            <Skeleton w="140px" h={10} style={{ marginTop: 10 }} />
          </div>
          <Skeleton w="170px" h={32} />
        </div>

        <div className="yx-card-body">
          <div className="d-flex flex-wrap" style={{ gap: 10 }}>
            <SkeletonPill w={180} h={34} />
            <SkeletonPill w={220} h={34} />
          </div>

          <div className="yx-divider" />

          <div className="d-flex align-items-center" style={{ gap: 14, marginBottom: 12 }}>
            <div style={{ width: 170, minWidth: 170 }}>
              <Skeleton w="140px" h={12} />
              <Skeleton w="80px" h={10} style={{ marginTop: 6 }} />
            </div>
            <div style={{ flex: 1 }}>
              <SkeletonBar />
            </div>
          </div>

          <div className="d-flex align-items-center" style={{ gap: 14 }}>
            <div style={{ width: 170, minWidth: 170 }}>
              <Skeleton w="140px" h={12} />
              <Skeleton w="80px" h={10} style={{ marginTop: 6 }} />
            </div>
            <div style={{ flex: 1 }}>
              <SkeletonBar />
            </div>
          </div>
        </div>

        <div className="yx-card-foot d-flex align-items-center justify-content-between">
          <Skeleton w="110px" h={10} />
          <SkeletonPill w={140} h={34} />
        </div>
      </div>
    );
  }

  return (
    <FinanceCardHead
      title="Net income"
      right={
        <span className="yx-pill yx-pill--muted" title="This financial year">
          Current FY <i className="fa fa-chevron-down ml-1" />
        </span>
      }
      value={net}
      currency={currency}
      subtitle="Net revenue"
      negativeParen={false} // net income usually shows as - rather than ( )
      formatValue={(v) => `${currency} ${numberFormat(v)}`}
    >
      {/* delta + chips */}
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
        {delta ? <span className="yx-pill yx-pill--muted">{delta}</span> : null}

        <Chip icon="fa-balance-scale" tone="neutral">
          Expense ratio {expenseRatio}%
        </Chip>

        <Chip icon="fa-clipboard-check" tone={reconciledPct != null ? "indigo" : "warn"}>
          {reconciledPct != null
            ? `${Math.round(reconciledPct)}% reconciled`
            : "Reconciliation pending"}
        </Chip>
      </div>

      <div className="yx-divider" />

      {/* bars */}
      <div className="d-flex flex-column" style={{ gap: 14 }}>
        <div className="d-flex align-items-center" style={{ gap: 14 }}>
          <div style={{ width: 170, minWidth: 170 }}>
            <div className="yx-row">
              {currency} {numberFormat(income)}
            </div>
            <div className="yx-sub">Income</div>
          </div>
          <Bar valuePct={incPct} tone="indigo" />
        </div>

        <div className="d-flex align-items-center" style={{ gap: 14 }}>
          <div style={{ width: 170, minWidth: 170 }}>
            <div className="yx-row">
              {currency} {numberFormat(expense)}
            </div>
            <div className="yx-sub">Expenses</div>
          </div>
          <Bar valuePct={expPct} tone="muted" />
        </div>
      </div>

      <div className="yx-divider" />

      {/* footer actions */}
      <div className="d-flex align-items-center justify-content-between">
        <Link to="/report-income-statement" className="yx-link d-inline-flex align-items-center">
          See P&amp;L report <i className="fa fa-arrow-right ml-2" />
        </Link>

        {hasUncat ? (
          <button className="yx-btn yx-btn--soft" type="button">
            <i className="fa fa-circle-exclamation" />
            Review {numberFormat(uncategorizedCount)}
          </button>
        ) : (
          <span />
        )}

       
      </div>
     
    </FinanceCardHead>
  );
};

export default ProfitAndLossCard;
